/**
 * Work plan edits — pure derivation.
 *
 * What a drop means: which shift window a new order lands in, how long it
 * runs on that line (from the product's standard), what its batch label is,
 * and where an existing order goes when its card is dragged to another day
 * or line. Nothing here touches React or the server; the controller saves.
 */
import { addDays, addMinutes, format, isSameDay, parseISO, set, startOfDay } from "date-fns";

import { LaborStandard } from "../../../../services/labor-standards-api";
import { Product } from "../../../../services/products-api";
import {
	operationDurationOn,
	operationRunsOn,
	sameWorkstation,
	standardHasStepOn,
} from "../../utils/standard-matching";
import { CreateWorkOrderDto, isNonProductionOrder,ManufacturingOrder } from "../utils/calculations"; // eslint-disable-line sort-imports

/** A shift window on the plant's clock, minutes from midnight. */
export interface ShiftWindow {
	label: string;
	startMinutes: number;
	endMinutes: number;
}

/** The plant's two shifts when the plan carries nothing to learn them from. */
export const DEFAULT_SHIFTS: ShiftWindow[] = [
	{ label: "1st", startMinutes: 6 * 60, endMinutes: 14 * 60 + 30 },
	{ label: "2nd", startMinutes: 14 * 60 + 30, endMinutes: 23 * 60 },
];

/** Local ISO without zone, the shape the API stores and returns. */
export const toLocalIso = (date: Date): string => format(date, "yyyy-MM-dd'T'HH:mm:ss");

const minutesOfDay = (date: Date): number => date.getHours() * 60 + date.getMinutes();

/**
 * The shift windows the plan actually uses: the most common (start, end)
 * clock pairs among its production orders, up to three, in start order. A
 * plan with fewer than two distinct pairs falls back to the defaults.
 */
export function shiftWindowsFrom(orders: ManufacturingOrder[]): ShiftWindow[] {
	const counts = new Map<string, { start: number; end: number; n: number }>();
	orders
		.filter((o) => !isNonProductionOrder(o.orderType) && o.endTime)
		.forEach((o) => {
			const start = parseISO(o.startTime);
			const end = parseISO(o.endTime as string);
			const startMinutes = minutesOfDay(start);
			let endMinutes = minutesOfDay(end);
			if (!isSameDay(start, end)) endMinutes += 24 * 60;
			if (endMinutes - startMinutes < 4 * 60 || endMinutes - startMinutes > 13 * 60) return; // a batch, not a shift
			const key = `${startMinutes}-${endMinutes}`;
			const row = counts.get(key) ?? { start: startMinutes, end: endMinutes, n: 0 };
			row.n += 1;
			counts.set(key, row);
		});
	// A window seen once is a batch that happened to be long, not a shift.
	const top = Array.from(counts.values())
		.filter((row) => row.n >= 2)
		.sort((a, b) => b.n - a.n)
		.slice(0, 3)
		.sort((a, b) => a.start - b.start);
	if (top.length < 2) return DEFAULT_SHIFTS;
	const ordinal = ["1st", "2nd", "3rd"];
	return top.map((row, index) => ({ label: ordinal[index], startMinutes: row.start, endMinutes: row.end }));
}

const atMinutes = (day: Date, minutes: number): Date =>
	set(startOfDay(day), { hours: Math.floor(minutes / 60) % 24, minutes: minutes % 60, seconds: 0, milliseconds: 0 });

/** The window placed on a calendar day; an end past midnight rolls to the next day. */
export const windowOn = (day: Date, shift: ShiftWindow): { start: Date; end: Date } => {
	const start = atMinutes(day, shift.startMinutes);
	const end = shift.endMinutes >= 24 * 60 ? addDays(atMinutes(day, shift.endMinutes - 24 * 60), 1) : atMinutes(day, shift.endMinutes);
	return { start, end };
};

const overlaps = (aStart: Date, aEnd: Date, bStart: Date, bEnd: Date): boolean => aStart < bEnd && bStart < aEnd;

/** Orders on the line that overlap the window (the API allows one order per line at a time). */
export const ordersInWindow = (
	orders: ManufacturingOrder[],
	workstation: string,
	start: Date,
	end: Date,
	ignoreId?: number
): ManufacturingOrder[] =>
	orders.filter((o) => {
		if (o.id === ignoreId || !sameWorkstation(o.workstation, workstation)) return false;
		const oStart = parseISO(o.startTime);
		const oEnd = o.endTime ? parseISO(o.endTime) : addMinutes(oStart, 8 * 60);
		return overlaps(start, end, oStart, oEnd);
	});

/**
 * The first shift on the day that has nothing on this line yet, else null
 * when every shift is taken (the caller then offers to replace).
 */
export function firstFreeShift(
	orders: ManufacturingOrder[],
	workstation: string,
	day: Date,
	shifts: ShiftWindow[]
): ShiftWindow | null {
	return shifts.find((shift) => {
		const { start, end } = windowOn(day, shift);
		return ordersInWindow(orders, workstation, start, end).length === 0;
	}) ?? null;
}

/** The standards that can run this product on this line, newest first. */
export const standardsFor = (product: Pick<Product, "id">, workstation: string, standards: LaborStandard[]): LaborStandard[] =>
	standards
		.filter((s) => s.isActive && s.productId === product.id && standardHasStepOn(s, workstation))
		.sort((a, b) => (b.createdDate ?? "").localeCompare(a.createdDate ?? ""));

/** Every line a product has a step on, across its standards. */
export const linesForProduct = (product: Pick<Product, "id">, standards: LaborStandard[]): string[] => {
	const names: string[] = [];
	standards
		.filter((s) => s.isActive && s.productId === product.id)
		.forEach((s) => {
			(s.operations ?? []).forEach((op) => {
				[op.workstation, ...(op.workstationOptions ?? []).map((o) => o.workstation)]
					.filter((n): n is string => Boolean(n && n.trim()))
					.forEach((n) => {
						if (!names.some((e) => sameWorkstation(e, n))) names.push(n.trim());
					});
			});
			(s.workstations ?? []).forEach((n) => {
				if (n && !names.some((e) => sameWorkstation(e, n))) names.push(n);
			});
		});
	return names;
};

/**
 * How long the product's step runs on this line, in minutes: the duration of
 * the standard's operation on that work center, else the standard's total,
 * else null (the caller then uses the shift).
 */
export function runMinutesOn(product: Pick<Product, "id">, workstation: string, standards: LaborStandard[]): number | null {
	const standard = standardsFor(product, workstation, standards)[0];
	if (!standard) return null;
	const operation = (standard.operations ?? []).find((op) => operationRunsOn(op, workstation));
	if (operation) {
		const minutes = operationDurationOn(operation, workstation);
		if (minutes > 0) return minutes;
	}
	return standard.totalDurationMinutes > 0 ? standard.totalDurationMinutes : null;
}

/**
 * The batch label for a product's next order: when its recent orders end in
 * a number ("Meto 14", "MSA Drug Seal 30") the next number; an uploaded run's
 * "n/of" ("Oxycodone 15 mg · 2/2") counts by its n; else the product name.
 * Labels live in `sku`, which is what the card prints.
 */
export function nextBatchLabel(product: Pick<Product, "id" | "name">, orders: ManufacturingOrder[]): string {
	const labels = orders
		.filter((o) => o.productId === product.id && (o.sku ?? "").trim())
		.map((o) => (o.sku as string).trim());
	let bestPrefix: string | null = null;
	let bestNumber = -1;
	labels.forEach((label) => {
		const match = /^(.*?)(\d+)(?:\s*\/\s*\d+)?\s*$/.exec(label);
		if (!match) return;
		const number = Number(match[2]);
		if (number > bestNumber) {
			bestNumber = number;
			bestPrefix = match[1];
		}
	});
	if (bestPrefix !== null && bestNumber >= 0) return `${bestPrefix}${bestNumber + 1}`;
	return product.name;
}

export interface DropPlan {
	/** The order to create, ready for the API. */
	order: CreateWorkOrderDto;
	shift: ShiftWindow;
	/** False when the product has no standard with a step on this line (the order still saves; the roster will warn). */
	hasStandard: boolean;
	/** Set when every shift on the day is taken: the order that would have to go. */
	blockedBy: ManufacturingOrder | null;
}

/**
 * What dropping a product on a line/day cell creates. The order takes the
 * first free shift on that line (or the one asked for), runs for the
 * product's step duration clipped to the shift, and gets the next batch
 * label.
 */
export function planDrop(args: {
	product: Pick<Product, "id" | "name" | "itemNumber">;
	workstation: string;
	day: Date;
	orders: ManufacturingOrder[];
	standards: LaborStandard[];
	shifts: ShiftWindow[];
	shift?: ShiftWindow | null;
	locationId?: number | null;
	/** "production" (default) or a non-production kind such as "cleaning"; those take the product name as label. */
	orderType?: string;
	/** The label to use, when the caller already looked it up plant-wide; else derived from the loaded orders. */
	label?: string | null;
}): DropPlan {
	const { product, workstation, day, orders, standards, shifts, locationId } = args;
	const orderType = args.orderType ?? "production";
	const free = args.shift ?? firstFreeShift(orders, workstation, day, shifts);
	const shift = free ?? shifts[0];
	const { start, end } = windowOn(day, shift);
	const minutes = runMinutesOn(product, workstation, standards);
	const shiftMinutes = (end.getTime() - start.getTime()) / 60000;
	const runEnd = minutes ? addMinutes(start, Math.min(minutes, shiftMinutes)) : end;
	const blocking = free ? [] : ordersInWindow(orders, workstation, start, end);
	const standard = standardsFor(product, workstation, standards)[0];
	return {
		order: {
			productId: product.id,
			sku: orderType === "production" ? args.label || nextBatchLabel(product, orders) : product.name,
			quantity: 1,
			workstation,
			startTime: toLocalIso(start),
			endTime: toLocalIso(runEnd),
			laborStandardId: standard?.id,
			orderType,
			locationId: locationId ?? undefined,
		},
		shift,
		hasStandard: Boolean(standard),
		blockedBy: blocking[0] ?? null,
	};
}

export interface MovePlan {
	startTime: string;
	endTime?: string;
	workstation: string;
	/** The order already on the target line in that window, if any. */
	blockedBy: ManufacturingOrder | null;
	/** False when the product has no step on the new line. */
	hasStandard: boolean;
}

/**
 * Where an order goes when its card is dropped on another cell: same clock
 * times, shifted by whole days, on the target line.
 */
export function planMove(args: {
	order: ManufacturingOrder;
	workstation: string;
	day: Date;
	orders: ManufacturingOrder[];
	standards: LaborStandard[];
}): MovePlan {
	const { order, workstation, day, orders, standards } = args;
	const start = parseISO(order.startTime);
	const end = order.endTime ? parseISO(order.endTime) : null;
	const dayDelta = Math.round((startOfDay(day).getTime() - startOfDay(start).getTime()) / 86400000);
	const newStart = addDays(start, dayDelta);
	const newEnd = end ? addDays(end, dayDelta) : null;
	const blocking = ordersInWindow(orders, workstation, newStart, newEnd ?? addMinutes(newStart, 8 * 60), order.id);
	const hasStandard = isNonProductionOrder(order.orderType)
		|| !order.productId
		|| standards.some((s) => s.isActive && s.productId === order.productId && standardHasStepOn(s, workstation));
	return {
		startTime: toLocalIso(newStart),
		endTime: newEnd ? toLocalIso(newEnd) : undefined,
		workstation,
		blockedBy: blocking[0] ?? null,
		hasStandard,
	};
}

/** The shift an order sits in, by its start clock; null when it matches none. */
export const shiftOf = (order: Pick<ManufacturingOrder, "startTime">, shifts: ShiftWindow[]): ShiftWindow | null => {
	const minutes = minutesOfDay(parseISO(order.startTime));
	return shifts.find((s) => Math.abs(s.startMinutes - minutes) <= 60) ?? null;
};

/**
 * The order kind an activity product stands for, by its name; null for a
 * real product. Activity products are the demo's cleaning / changeover /
 * swab / hold / maintenance / setup standards (item numbers "ACT-…").
 */
export const activityOrderType = (product: Pick<Product, "name" | "itemNumber">): string | null => {
	if (!(product.itemNumber ?? "").toUpperCase().startsWith("ACT-")) return null;
	const name = product.name.toLowerCase();
	if (name.includes("clean") || name.includes("swab")) return "cleaning";
	if (name.includes("changeover")) return "changeover";
	if (name.includes("hold")) return "inspection";
	if (name.includes("maintenance") || /\bpm\b/.test(name)) return "maintenance";
	if (name.includes("setup") || name.includes("set-up")) return "setup";
	return "cleaning";
};

/**
 * Finish rows coming in from an upload the way a drop would make them: the
 * plant they belong to, the labor standard for that product on that line,
 * and — when the file gave no end — the standard's run time (capped by
 * nothing; the planner edits it on the board if the file was short).
 */
export function completeUploadedOrders<T extends { productId?: number | null; workstation: string; startTime: string; endTime?: string | null; laborStandardId?: number | null; locationId?: number | null }>(
	orders: T[],
	standards: LaborStandard[],
	locationId: number | null | undefined
): T[] {
	return orders.map((order) => {
		const product = order.productId ? { id: order.productId } : null;
		const standard = product ? standardsFor(product, order.workstation, standards)[0] : undefined;
		const minutes = product && !order.endTime ? runMinutesOn(product, order.workstation, standards) : null;
		const endTime = order.endTime || (minutes ? toLocalIso(new Date(new Date(order.startTime).getTime() + minutes * 60000)) : order.endTime);
		return {
			...order,
			locationId: order.locationId ?? locationId ?? null,
			laborStandardId: order.laborStandardId ?? standard?.id,
			endTime: endTime || undefined,
		};
	});
}
